import React, { useRef, useState } from 'react'
import * as yup from 'yup'

import {
  Button,
  Separator,
  Form as BaseForm,
  Input,
  Modal,
  LearnMoreTooltip,
} from '@sketch/components'

import {
  useFlag,
  InvitePeopleFieldForm,
  AccessLevelSelect,
  AccessLevelSelectOption,
  AccessLevelSelectSeparator,
  AccessLevelSelectRemoveButton,
  AccessLevelSelectBanner,
  WorkspaceRow,
} from '@sketch/modules-common'
import { ProjectAccessSelect } from 'modules/projects/components/ProjectAccessSelect/ProjectAccessSelect'
import { WorkspaceMemberRow } from 'modules/projects/components/MemberRow'
import useUserProfile from 'modules/user/useUserProfile'
import { useAutocomplete } from './useAutocomplete'
import {
  ProjectMembershipFragment,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'
import type {
  CreateProjectWorkspaceAccessLevel,
  FormikRenderProps,
  PendingProjectMember,
  PendingGuestMember,
} from './types'
import {
  MembersList,
  StyledInvitePeopleField,
  StyledInviteButton,
} from './AddNewProjectModal.styles'
import {
  AddProjectMemberPermissionsAccessSelect,
  ProjectMemberPermissionsAccessSelect,
} from 'modules/projects/components/ProjectMemberPermissionsAccessSelect/ProjectMemberPermissionsAccessSelect'
import { SKETCH_WEBSITE } from '@sketch/env-config'

/*
TODO: Use the correct types when this components update to Typescript
https://github.com/sketch-hq/Cloud/issues/1690
*/
const Form = BaseForm as any

const emailValidation = yup.string().trim().required().lowercase().email()

export const FormikRender = (props: FormikRenderProps) => {
  const hasGuestsInProjects = useFlag('guests-in-project')
  const [membersList, setMembersList] = useState<PendingProjectMember[]>([])
  const [guestPermissions, setGuestPermissions] = useState<{
    guestCommentsEnabled: boolean
    guestInspectEnabled: boolean
  }>({
    guestCommentsEnabled: true,
    guestInspectEnabled: true,
  })

  const { formikBag, hideModal, workspace } = props
  const containerRef = useRef<HTMLInputElement>(null)
  const { data: userData } = useUserProfile()
  const workspaceIdentifier = workspace.identifier

  const currentUser = {
    avatar: userData?.me.avatar,
    name: userData?.me.name,
    identifier: userData?.me.identifier,
    email: userData?.me.email,
  }
  // We need to do this to render the current user membership row, as we don't have the
  // workspace membership of the current user easily accesible.
  //
  // There's no need to add it when submitting the form, this is only visual.
  // BE adds automatically the workspace membership of the project creator
  const normalizedCurrentUser = {
    user: currentUser,
  } as WorkspaceMembershipFragment

  const {
    errors,
    values,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    isValid,
  } = formikBag

  const autocomplete = useAutocomplete({
    workspaceIdentifier,
    membersList: membersList
      .filter(member => Boolean(member.workspaceMembershipIdentifier))
      .map(member => member.workspaceMembershipIdentifier) as string[],
    options: {
      // This is necessary for filtering the current user on the
      // autocomplete list
      userIdentifier: currentUser.identifier,
    },
  })

  const addMember = () => {
    const selectedItem = autocomplete.combobox.selectedItem

    if (selectedItem) {
      const pendingMember: PendingProjectMember = {
        workspaceMembershipIdentifier: selectedItem.identifier,
        workspaceMembership: selectedItem,
        privateAccessLevel: selectedItem.isEditor ? 'EDIT' : 'VIEW',
      }
      setMembersList([...membersList, pendingMember])
      autocomplete.combobox.reset()
      return
    }

    if (autocomplete.combobox.inputValue) {
      const pendingGuest: PendingProjectMember = {
        email: autocomplete.combobox.inputValue,
        privateAccessLevel: 'VIEW',
        guestCommentsEnabled: guestPermissions.guestCommentsEnabled,
        guestInspectEnabled: guestPermissions.guestInspectEnabled,
        workspaceMembership: {
          role: 'GUEST',
          user: null,
          invite: {
            __typename: 'WorkspaceInvite',
            identifier: '',
            email: autocomplete.combobox.inputValue,
          },
        },
      }
      setMembersList([...membersList, pendingGuest])
      autocomplete.combobox.reset()
      return
    }
  }

  const deleteMember = (index: number) => {
    membersList.splice(index, 1)
    setMembersList([...membersList])
  }

  const setWorkspacePermissions = (
    level: CreateProjectWorkspaceAccessLevel
  ) => {
    setFieldValue('projectWorkspaceAccessLevel', level)
  }

  const cleanMembersList = () => {
    const projectMembersDefaultList: {
      members: string[]
      guests: PendingGuestMember[]
    } = {
      members: [],
      guests: [],
    }

    const projectMembersList = membersList.reduce((list, currentMember) => {
      if (currentMember.workspaceMembershipIdentifier) {
        list.members.push(currentMember.workspaceMembershipIdentifier)
      }

      if (currentMember.email) {
        list.guests.push({
          email: currentMember.email,
          guestCommentsEnabled: currentMember.guestCommentsEnabled!,
          guestInspectEnabled: currentMember.guestInspectEnabled!,
        })
      }

      return list
    }, projectMembersDefaultList)

    setFieldValue('workspaceMembershipIdentifiers', projectMembersList.members)
    setFieldValue('guestsToInvite', projectMembersList.guests)
  }

  const submitForm = async () => {
    cleanMembersList()
    await handleSubmit()
  }

  const isInputValid =
    autocomplete.isValueSelected ||
    (hasGuestsInProjects &&
      emailValidation.isValidSync(autocomplete.combobox.inputValue))

  return (
    <Modal title="Create a New Project" onCancel={hideModal}>
      <Form
        onSubmit={(e: Event) => {
          e.preventDefault()
          submitForm()
        }}
      >
        <Modal.Body>
          <p>
            Use projects to organize and share Sketch documents.{' '}
            <LearnMoreTooltip
              href={`${SKETCH_WEBSITE}/docs/workspaces/organizing-your-workspace/`}
              tooltipContent="Learn more about using projects"
              tooltipPlacement="top"
            />
          </p>
          <Form.Field
            name="projectName"
            label="Project Name"
            errorText={touched.projectName && errors.projectName}
          >
            <Input
              name="projectName"
              type="text"
              placeholder="Give your project a name"
              aria-label="Project name input field"
              value={values.projectName}
              onChange={handleChange}
              disabled={isSubmitting}
              autoFocus={values.projectName.length === 0}
            />
          </Form.Field>
          <Separator mt={4} mb={6} />
          <InvitePeopleFieldForm>
            <StyledInvitePeopleField
              label="Manage Access"
              aria-label="Invite people input field"
              containerRef={containerRef}
              autocomplete={autocomplete}
              placeholder="Add a name or email address"
              dynamicAction={props => {
                const workspaceMembership = props.selectedItem as Partial<WorkspaceMembershipFragment>
                const membership = {
                  workspaceMembership: workspaceMembership ?? {
                    __typename: 'WorkspaceMembership',
                    role: 'GUEST',
                  },
                  privateAccessLevel: workspaceMembership?.isEditor
                    ? ('EDIT' as ProjectMembershipFragment['privateAccessLevel'])
                    : ('VIEW' as ProjectMembershipFragment['privateAccessLevel']),
                }

                return (
                  <AddProjectMemberPermissionsAccessSelect
                    member={membership}
                    guestCommentsEnabled={guestPermissions.guestCommentsEnabled}
                    guestInspectEnabled={guestPermissions.guestInspectEnabled}
                    onPermissionChange={(permission, value) => {
                      setGuestPermissions({
                        ...guestPermissions,
                        [permission]: value,
                      })
                    }}
                  />
                )
              }}
            />
            <StyledInviteButton
              size="32"
              onClick={addMember}
              name="Add member to project"
              disabled={!isInputValid}
            >
              Add
            </StyledInviteButton>
          </InvitePeopleFieldForm>
          <MembersList>
            <WorkspaceRow
              workspace={workspace}
              actions={
                <ProjectAccessSelect
                  level={values.projectWorkspaceAccessLevel}
                  setLevel={setWorkspacePermissions}
                  hideCommentLabel
                />
              }
            />
            <WorkspaceMemberRow
              key={currentUser.identifier}
              workspaceMembership={normalizedCurrentUser}
              actions={
                <AccessLevelSelect
                  level="EDIT"
                  setLevel={() => false}
                  hideCommentLabel
                >
                  <AccessLevelSelectOption
                    level="EDIT"
                    label="Edit"
                    help="Edit and view the document"
                    disabled
                  />
                  <AccessLevelSelectBanner>
                    Access is determined by the Member&prime;s Workspace role.
                  </AccessLevelSelectBanner>
                </AccessLevelSelect>
              }
            />
            {membersList.map((member, index) => {
              return (
                <WorkspaceMemberRow
                  key={member.workspaceMembershipIdentifier || member.email}
                  workspaceMembership={member.workspaceMembership}
                  actions={
                    <AccessLevelSelect
                      level={member.privateAccessLevel}
                      setLevel={() => {}}
                      hideCommentLabel
                    >
                      <ProjectMemberPermissionsAccessSelect
                        member={{
                          privateAccessLevel: member.privateAccessLevel,
                          workspaceMembership: member.workspaceMembership,
                        }}
                        guestCommentsEnabled={member.guestCommentsEnabled}
                        guestInspectEnabled={member.guestInspectEnabled}
                        onPermissionChange={(permission, value) => {
                          membersList[index][permission] = value
                          setMembersList([...membersList])
                        }}
                      />
                      <AccessLevelSelectSeparator />
                      <AccessLevelSelectRemoveButton
                        onClick={() => deleteMember(index)}
                      >
                        Remove Member
                      </AccessLevelSelectRemoveButton>
                    </AccessLevelSelect>
                  }
                />
              )
            })}
          </MembersList>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isSubmitting} type="button" onClick={hideModal}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || isSubmitting}
            name="Create Project"
          >
            Create Project
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
